import React from 'react';
import { FaFileMedical, FaFilePdf } from 'react-icons/fa';

// Health data: a prescription file name can carry the patient's name, so nothing
// in here is ever logged.

// Extensions the upload endpoint accepts (loan-and-subscription
// prescription_documents.ALLOWED_EXTENSIONS). Anything else renders as a file
// glyph rather than a broken <img>.
const IMAGE_PATTERN = /\.(jpe?g|png|webp|heic|heif)(\?|$)/i;

const isImage = (doc) => IMAGE_PATTERN.test(doc?.name || doc?.url || '');

const openInNewTab = (url) => {
  // noopener/noreferrer: the asset host must not get a handle on the admin window.
  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Read-only view of the prescription files the subscriber attached to a medicine
 * order. Rendered on both admin screens that already show the delivery address —
 * the ops cart (MedicineTransaction) and the booking detail (MedicineBookingProfile).
 *
 * Clicking a file opens it in a new tab, where the browser's own viewer handles
 * saving it. There is deliberately no download button: the file is served from
 * the asset host (AWS_S3_BASE_URL), a different origin from admin, which sends no
 * Access-Control-Allow-Origin header — an in-app download would be blocked and an
 * <a download> attribute is ignored cross-origin.
 *
 * Ops can view only. Attaching and removing stays with the subscriber in their app.
 */
const PrescriptionAttachments = ({ documents = [], className = '' }) => {
  const files = Array.isArray(documents) ? documents : [];

  return (
    <div className={`border border-gray-200 rounded-lg p-4 bg-gray-50 ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <FaFileMedical className="text-blue-600 w-4 h-4" />
          Prescription
        </div>
        {files.length > 0 && (
          <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-50 text-blue-700 border border-blue-200">
            {files.length} {files.length === 1 ? 'file' : 'files'}
          </span>
        )}
      </div>

      {files.length === 0 ? (
        <p className="text-sm text-gray-400">No prescription uploaded for this order.</p>
      ) : (
        <>
          {/* Up to 5 files can be attached (cap enforced at upload); the row
              scrolls rather than wrapping so the block keeps its height. */}
          <div className="flex flex-nowrap items-start gap-3 overflow-x-auto pb-1">
            {files.map((doc) => (
              <button
                key={doc.id}
                type="button"
                onClick={() => openInNewTab(doc.url)}
                title={doc.name || 'Prescription'}
                className="shrink-0 w-[104px] h-[88px] rounded-lg border border-gray-200 bg-white overflow-hidden flex items-center justify-center hover:border-blue-400 hover:shadow-sm transition cursor-pointer"
              >
                {isImage(doc) ? (
                  <img
                    src={doc.url}
                    alt="Prescription"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-1 px-2">
                    <FaFilePdf className="text-blue-700 w-5 h-5" />
                    <span className="text-[10px] text-gray-500 truncate max-w-[88px]">
                      {doc.name || 'Document'}
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-gray-400">
            Click a file to open it in a new tab, where it can be downloaded.
          </p>
        </>
      )}
    </div>
  );
};

export default PrescriptionAttachments;
